import React from 'react'

import { SubscriptionDto, SubscriptionTierDto } from '@/shared/apis/fastmile/models/generated'
import { pxToRem } from '@/shared/styles/themes'
import { faExternalLink, faInfoCircle } from '@fortawesome/pro-regular-svg-icons'
import { faCheckCircle, faTimesCircle } from '@fortawesome/pro-solid-svg-icons'
import { Box, Card, CardContent, Chip, Grid2 as Grid, LinearProgress, styled, Typography } from '@mui/material'

import { Button } from '../Button/Button'
import { Icon } from '../Icon/Icon'

export type CurrentSubscriptionTierProps = {
  subscription: SubscriptionDto
  tier: SubscriptionTierDto | null
  description: string
  features: FeatureDetail[]
  onManageBilling: () => void
  onUpgrade: () => void
  isManageBillingLoading?: boolean
  isUpgradeLoading?: boolean
  currentVehicleUsage?: number // Current number of vehicles in use
  showUpgradeOption?: boolean
}

export type FeatureDetail = {
  title: string
  details: string
  available: 'yes' | 'no' | 'neutral'
}

export const CurrentSubscriptionTier: React.FC<CurrentSubscriptionTierProps> = ({
  subscription,
  description,
  features,
  onManageBilling,
  isManageBillingLoading = false,
  currentVehicleUsage,
}) => {
  const getStatusChip = () => {
    const status = subscription.status

    switch (status) {
      case 1:
        return <Chip label="Active" color="success" size="small" />
      case 2:
        return <Chip label="Cancelled" color="error" size="small" />
      case 3:
        return <Chip label="Past Due" color="warning" size="small" />
      case 4:
        return <Chip label="Incomplete" color="info" size="small" />
      default:
        return <Chip label="Unknown" color="default" size="small" />
    }
  }

  const getUsagePercentage = () => {
    if (!currentVehicleUsage || !subscription.vehicleQuantity) return 0

    return (currentVehicleUsage / subscription.vehicleQuantity) * 100
  }

  const getUsageColor = () => {
    const percentage = getUsagePercentage()

    if (percentage >= 90) return 'error'
    if (percentage >= 75) return 'warning'

    return 'primary'
  }

  const getAvailableIcon = (available: 'yes' | 'no' | 'neutral') => {
    if (available === 'yes')
      return <Icon icon={faCheckCircle} color="success" sx={{ mr: pxToRem(6) }} />
    if (available === 'no')
      return <Icon icon={faTimesCircle} color="error" sx={{ mr: pxToRem(6) }} />

    return <Icon icon={faInfoCircle} color="info" sx={{ mr: pxToRem(6) }} />
  }

  return (
    <StyledCard>
      <CardContent>
        <StyledHeader>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
                {subscription.tierName}
              </Typography>
              {getStatusChip()}
            </Box>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Box>
        </StyledHeader>

        <StyledUsageSection>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Fleet Usage
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">
                Runs: {currentVehicleUsage || 0} / {subscription.vehicleQuantity}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {Math.round(getUsagePercentage())}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={getUsagePercentage()}
              color={getUsageColor()}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
          {getUsagePercentage() >= 90 && getUsagePercentage() <= 100 && (
            <Typography variant="caption" color="error.main">
              You're approaching your vehicle limit. Consider upgrading your plan.
            </Typography>
          )}
          {getUsagePercentage() > 100 && (
            <Typography variant="caption" color="error.main">
              You have reached your vehicle limit. Please upgrade your plan to continue operations without interruption.
            </Typography>
          )}
        </StyledUsageSection>

        <StyledActionsSection>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Button
                fullWidth
                outlined
                size={'large'}
                onClick={onManageBilling}
                disabled={isManageBillingLoading}
                startIcon={<Icon icon={faExternalLink} />}
              >
                {isManageBillingLoading ? 'Opening...' : 'Manage Billing'}
              </Button>
            </Grid>
          </Grid>
        </StyledActionsSection>

        <StyledFeaturesSection>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Plan Features
          </Typography>
          <StyledFeaturesList>
            {features.map((feature, index) => (
              <StyledFeatureItem key={index}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                  {getAvailableIcon(feature.available)}
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {feature.title}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 3 }}>
                  {feature.details}
                </Typography>
              </StyledFeatureItem>
            ))}
          </StyledFeaturesList>
        </StyledFeaturesSection>
      </CardContent>
    </StyledCard>
  )
}

const StyledCard = styled(Card)`
  border-radius: ${pxToRem(12)};
  border: 1px solid ${(props) => props.theme.palette.divider};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`

const StyledHeader = styled(Box)`
  margin-bottom: ${(props) => props.theme.spacing(3)};
  padding-bottom: ${(props) => props.theme.spacing(2)};
  border-bottom: 1px solid ${(props) => props.theme.palette.divider};
`

const StyledUsageSection = styled(Box)`
  margin-bottom: ${(props) => props.theme.spacing(3)};
  border-radius: ${pxToRem(8)};
`

const StyledActionsSection = styled(Box)`
  margin-bottom: ${(props) => props.theme.spacing(3)};
`

const StyledFeaturesSection = styled(Box)`
  padding-top: ${(props) => props.theme.spacing(2)};
  border-top: 1px solid ${(props) => props.theme.palette.divider};
`

const StyledFeaturesList = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing(2)};
`

const StyledFeatureItem = styled(Box)`
  padding: ${(props) => props.theme.spacing(1)};
  border-radius: ${pxToRem(4)};
  background-color: ${(props) => props.theme.palette.background.default};
`